import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ExploreAggColumnType,
  exploreAggColumnTypeToLabel,
  ExploreColumnType,
  exploreColumnTypeToLabel,
  exploreGroupByToColumn,
  isExploreAggColumnType,
  isExploreColumnType,
  isExploreGroupByColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import {
  ChartColumnIncreasingIcon,
  BarChartIcon,
  PieChartIcon,
  ChartLineIcon,
} from "lucide-react";
import { ExploreBarChart } from "../explore-widgets/ExploreBarChart";
import { ExploreStackedBarChart } from "../explore-widgets/ExploreStackedBarChart";
import { ExploreInteractionType, ExploreCustomField } from "@/v2/entities/explore/Explore.model";
import { Flex, Grid, Text } from "@radix-ui/themes";
import { ExplorePieChart } from "../explore-widgets/ExplorePieChart";
import Select from "@/components/shared/select";
import { ExploreOverTime } from "../explore-widgets/ExploreOverTime";
import { ExploreCount } from "../explore-widgets/ExploreCount";
import {
  visibleTicketAggColumns as allowedTicketsMetricOptions,
  visibleConversationAggColumns as allowedConversationsMetricOptions,
} from "@/v2/entities/explore/columns/useExploreColumns";
import { useExploreCustomFields } from "./useExploreCustomFields";
import { useExploreConversationCustomFieldsForView } from "./useExploreConversationCustomFieldsForView";

const allowedTicketsColumnOptions: ExploreColumnType[] = [
  "status",
  "channel",
  "topic",
  "category",
  "csat",
  "subTopic",
  "agentAssigned",
  "team",
  "resolutionStatus",
  "escalationReason",
  "skipReason",
  "avgQaScoreRoundRange",
  "avgAutoQaScore",
  "isQuackAnswered",
  "isAnsweredByAui",
];

export const allowedConversationsColumnOptions: ExploreColumnType[] = [
  "status",
  "channel",
  "topic",
  "category",
  "csat",
  "subTopic",
  "resolutionStatus",
  "escalationReason",
  "skipReason",
  "resolvedReason",
  "avgQaScoreRoundRange",
  "avgQuackMessageScoreRoundRange",
  "avgAutoQaScore",
  "isQuackAnswered",
  "isAnsweredByAui",
];

const chartLabels: Record<
  ChartTypes,
  { label: string; icon: React.ReactNode }
> = {
  pie: {
    label: "Pie",
    icon: <PieChartIcon size={16} />,
  },
  bar: {
    label: "Bar",
    icon: <BarChartIcon size={16} />,
  },
  count: {
    label: "Count",
    icon: <ChartColumnIncreasingIcon size={16} />,
  },
  overtime: {
    label: "Overtime",
    icon: <ChartLineIcon size={16} />,
  },
  bar_stacked: {
    label: "Stacked Bar",
    icon: <BarChartIcon size={16} />,
  },
  line: {
    label: "Line",
    icon: <ChartLineIcon size={16} />,
  },
};

const chartTypes = [
  "count",
  "pie",
  "bar",
  "bar_stacked",
  "overtime",
  "line",
] as const;
type ChartTypes = (typeof chartTypes)[number];

function buildStackedBarColumnOptions(
  source: ExploreInteractionType,
  groupBy: string | undefined,
  customFieldsBySource: ExploreCustomField[]
): { label: string; value: string }[] {
  const baseOptions =
    source === "tickets"
      ? allowedTicketsColumnOptions
      : allowedConversationsColumnOptions;
  const base = baseOptions
    .filter((opt) => !groupBy?.includes(opt))
    .map((opt) => ({
      label: exploreColumnTypeToLabel[opt],
      value: opt,
    }));
  const customOptions = customFieldsBySource.map((f) => ({
    label: f.label,
    value: f.id,
  }));
  return [...base, ...customOptions];
}

export const ExploreChartWizard = ({
  source,
  groupBy,
  hiddenFilters,
}: {
  source: ExploreInteractionType;
  groupBy?: string;
  hiddenFilters: Filter;
}) => {
  const customFields = useExploreCustomFields();
  const conversationCustomFieldsForView =
    useExploreConversationCustomFieldsForView();
  const customFieldsBySource =
    source === "conversations" ? conversationCustomFieldsForView : customFields;

  const stackedBarColumnOptions = useMemo(
    () => buildStackedBarColumnOptions(source, groupBy, customFieldsBySource),
    [source, groupBy, customFieldsBySource]
  );

  const [chartType, setChartType] = useState<ChartTypes>(chartTypes[0]);
  const [column, setColumn] = useState<string>(() =>
    source === "tickets"
      ? allowedTicketsColumnOptions[0]
      : allowedConversationsColumnOptions[0]
  );
  const [metric, setMetric] = useState<ExploreAggColumnType>("volume");

  const columnOptions = useMemo(() => {
    if (stackedBarColumnOptions.length === 0) {
      return (
        source === "tickets"
          ? allowedTicketsColumnOptions
          : allowedConversationsColumnOptions
      )
        .filter((opt) => !groupBy?.includes(opt))
        .map((opt) => ({
          label: exploreColumnTypeToLabel[opt],
          value: opt,
        }));
    }
    return stackedBarColumnOptions;
  }, [source, groupBy, stackedBarColumnOptions]);

  useEffect(() => {
    if (
      columnOptions.length > 0 &&
      !columnOptions.some((opt) => opt.value === column)
    ) {
      setColumn(columnOptions[0].value);
    }
  }, [columnOptions, column]);

  const metricOptions = useMemo(() => {
    return (
      source === "tickets"
        ? allowedTicketsMetricOptions
        : allowedConversationsMetricOptions
    )
      .filter((opt) => opt !== "countAgentsAssigned")
      .map((opt) => ({
        label: exploreAggColumnTypeToLabel[opt],
        value: opt,
      }));
  }, [source]);

  const splitBy = useMemo<string>(() => {
    if (groupBy == null) {
      return "tenantId";
    }
    if (isExploreGroupByColumnType(groupBy)) {
      return exploreGroupByToColumn[groupBy] ?? "tenantId";
    }
    return groupBy;
  }, [groupBy]);

  const handleColumnChange = useCallback(
    (value: string) => {
      setColumn(
        columnOptions.some((opt) => opt.value === value)
          ? value
          : columnOptions[0]?.value ?? ""
      );
    },
    [columnOptions]
  );

  const handleMetricChange = useCallback((metric: string) => {
    setMetric(isExploreAggColumnType(metric) ? metric : metricOptions[0].value);
  }, []);

  return (
    <Flex className="h-[600px] p-4 gap-4">
      <Flex
        flexShrink={"0"}
        p={"4"}
        direction={"column"}
        gap={"4"}
        className="bg-background rounded-lg border border-[var(--q-border)] shadow-sm"
      >
        <Flex direction={"column"} gap={"4"} width={"240px"}>
          <ToggleGroup.Root
            type="single"
            value={chartType}
            onValueChange={(val) => {
              if (val) setChartType(val as ChartTypes);
            }}
          >
            <Grid gap="2" columns="2">
              {chartTypes.map((type) => (
                <ToggleGroup.Item
                  key={type}
                  value={type}
                  aria-label={type}
                  style={{
                    padding: 10,
                    borderRadius: 6,
                    border: "1px solid var(--gray-4, #d1d5db)",
                    background:
                      chartType === type
                        ? "var(--gray-4, #e5e7eb)"
                        : "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span className="flex items-center gap-1 flex-col text-center align-middle justify-center w-full text-xs">
                    {chartLabels[type].icon}
                    {chartLabels[type].label}
                  </span>
                </ToggleGroup.Item>
              ))}
            </Grid>
          </ToggleGroup.Root>

          {["bar_stacked"].includes(chartType) && (
            <Flex direction={"column"} gap={"2"}>
              <Text size={"1"} color={"gray"} weight={"medium"} highContrast>
                Metric
              </Text>

              <Select
                options={columnOptions}
                value={column}
                onChange={handleColumnChange}
                size="medium"
                placeholder="Select Metric..."
              />
            </Flex>
          )}

          {["overtime", "line"].includes(chartType) && (
            <Flex direction={"column"} gap={"2"}>
              <Text size={"1"} color={"gray"} weight={"medium"} highContrast>
                Metric
              </Text>

              <Select
                options={metricOptions}
                value={metric}
                onChange={handleMetricChange}
                size="medium"
                placeholder="Select Metric..."
              />
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex
        flexGrow={"1"}
        className="bg-background rounded-lg border border-[var(--q-border)] shadow-sm"
      >
        {chartType === "count" && (
          <ExploreCount source={source} filter={hiddenFilters} />
        )}
        {chartType === "pie" && (
          <ExplorePieChart
            source={source}
            groupBy={groupBy}
            filter={hiddenFilters}
          />
        )}
        {chartType === "bar" && (
          <ExploreBarChart
            source={source}
            groupBy={groupBy}
            filter={hiddenFilters}
          />
        )}
        {chartType === "bar_stacked" && (
          <ExploreStackedBarChart
            source={source}
            groupBy={groupBy ?? "tenantId"}
            splitBy={column}
            filter={hiddenFilters}
          />
        )}
        {chartType === "overtime" && (
          <ExploreOverTime
            variant="area"
            source={source}
            splitBy={splitBy}
            metric={metric}
            filter={hiddenFilters}
          />
        )}
        {chartType === "line" && (
          <ExploreOverTime
            variant="line"
            source={source}
            splitBy={splitBy}
            metric={metric}
            filter={hiddenFilters}
          />
        )}
      </Flex>
    </Flex>
  );
};
